import { useEffect, useState } from 'react'
import cn from 'classnames'
import { Screen } from './Frame'
import { useStepFooter } from './hooks'
import { seatCount, useFirstRun } from './store'
import { BUDGETS, DEMO_TEAM, leadsFor, SEAT_EXTRA, SEAT_FIRST } from './data'
import { Avatar, Button, GoogleG, Icon, Input, LinkButton, MicrosoftM, Radio, SearchPick, Tile } from './ui'

const FUB_KEY_URL = 'https://app.followupboss.com/2/api'

/** A stacked card of rows, like the one in the layout. */
export const RowCard = ({ children, className, testId }: { children: React.ReactNode; className?: string; testId?: string }) => (
   <div className={cn('rounded-[20px] border border-[var(--fr-border)] bg-white divide-y divide-[var(--fr-border)] overflow-hidden', className)} data-testid={testId}>
      {children}
   </div>
)
export const Row = ({ children, className }: { children: React.ReactNode; className?: string }) => <div className={cn('flex items-center gap-4 px-5 min-h-[76px]', className)}>{children}</div>
/** A label above a field that is not an Input (search boxes, groups). */
export const FieldLabel = ({ children }: { children: React.ReactNode }) => <span className="block text-[13px] font-medium text-[var(--fr-text-2)] mb-1.5">{children}</span>
const IconDot = ({ name, tone = 'grey' }: { name: string; tone?: 'grey' | 'blue' | 'green' | 'black' }) => (
   <span className={cn('flex shrink-0 items-center justify-center w-10 h-10 rounded-full', tone === 'blue' && 'bg-[var(--fr-blue-tint)] text-[var(--fr-blue-dark)]', tone === 'green' && 'bg-[var(--fr-good-tint)] text-[var(--fr-good)]', tone === 'black' && 'bg-[var(--fr-black)] text-white', tone === 'grey' && 'bg-[var(--fr-surface-2)] text-[var(--fr-text)]')}>
      <Icon name={name} size={17} />
   </span>
)

/* ---------- 1. Connect ---------- */

export const ConnectStep = () => {
   const { fubConnected, set, next, direction } = useFirstRun()
   const [key, setKey] = useState('')
   const [checking, setChecking] = useState(false)
   const [help, setHelp] = useState(false)
   const looksValid = key.trim().length >= 8
   const verified = looksValid && !checking
   useEffect(() => {
      if (!looksValid || fubConnected) return
      setChecking(true)
      const t = setTimeout(() => setChecking(false), 700)
      return () => clearTimeout(t)
   }, [key, looksValid, fubConnected])
   const connect = () => {
      if (fubConnected) return next()
      set('fubConnected', true)
      setTimeout(next, 500)
   }
   useStepFooter(fubConnected ? 'Continue' : !key.trim() ? 'Paste your key to continue' : checking ? 'Checking your key' : 'Connect Follow Up Boss', connect, {
      disabled: !fubConnected && !verified,
      hint: fubConnected ? 'Your team, stages and lead sources are ready.' : 'We only read. Nothing in Follow Up Boss is changed.',
      testId: 'connect-fub',
   })
   return (
      <Screen stepKey="connect" direction={direction} kind="connect" title="Connect Follow Up Boss" lede="Paste your API key and we read your team, stages, tags and lead sources. Every screen after this comes filled in." width="sm">
         {fubConnected ? (
            <RowCard testId="fub-connected">
               <Row>
                  <IconDot name="check" tone="green" />
                  <div className="flex-1 min-w-0">
                     <p className="text-[15px] font-semibold">Patel Realty is connected</p>
                     <p className="text-[13px] text-[var(--fr-text-2)]">7 users · 12 stages · 9 tags · 4 lead sources</p>
                  </div>
               </Row>
            </RowCard>
         ) : (
            <>
               <Input label="Follow Up Boss API key" value={key} onChange={setKey} placeholder="Paste your key here" autoFocus onEnter={connect} testId="fub-key" leading={<Icon name="key" size={16} />} size="lg" success={verified ? <span className="inline-flex items-center gap-1"><Icon name="check" size={14} /> Key verified</span> : undefined} trailing={checking ? 'Checking' : undefined} />
               <div className="mt-4 text-center">
                  <LinkButton onClick={() => setHelp((v) => !v)}>{help ? 'Hide the steps' : 'Where do I find my API key?'}</LinkButton>
               </div>
               {help && (
                  <ol className="mt-3 list-decimal pl-6 text-[14px] text-[var(--fr-text-2)] space-y-1.5 rounded-2xl bg-[var(--fr-surface-2)] p-4 pl-9">
                     <li>
                        Open{' '}
                        <a className="text-[var(--fr-blue)] font-semibold" href={FUB_KEY_URL} target="_blank" rel="noreferrer">
                           Follow Up Boss → Admin → API
                        </a>
                     </li>
                     <li>Click “Create API Key” and name it “Forward Flow”.</li>
                     <li>Copy the key and paste it above. You need an admin or owner login.</li>
                  </ol>
               )}
            </>
         )}
      </Screen>
   )
}

/* ---------- 2. Is this you? ---------- */

export const YouStep = () => {
   const { owner, set, next, direction, email, calendar, team } = useFirstRun()
   const [editingPhone, setEditingPhone] = useState(false)
   const [phone, setPhone] = useState(owner.phone || '')
   const [picking, setPicking] = useState(false)
   const phoneOk = phone.replace(/\D/g, '').length === 10
   const save = () => {
      if (!phoneOk) return
      set('owner', { ...owner, phone })
      setEditingPhone(false)
   }
   useStepFooter(phoneOk ? 'Yes, that’s me' : 'Add your mobile number first', next, { disabled: !phoneOk || editingPhone, hint: 'Interested leads are transferred to this person, on this number.', testId: 'confirm-you' })
   return (
      <Screen stepKey="you" direction={direction} kind="you" title="Is this you?" lede="We matched your Follow Up Boss user to your sign-in. Check the name and the mobile number we hand leads to." width="sm">
         <RowCard testId="owner-card">
            <Row>
               <Avatar name={owner.name} size={40} />
               <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold truncate">{owner.name}</p>
                  <p className="text-[13px] text-[var(--fr-text-2)] truncate">{email || owner.email} · {owner.role}</p>
               </div>
               <LinkButton onClick={() => setPicking((v) => !v)}>{picking ? 'Cancel' : 'Not me'}</LinkButton>
            </Row>
            <Row>
               <IconDot name="phone" tone="blue" />
               {editingPhone ? (
                  <div className="flex-1 flex items-end gap-2 py-3">
                     <Input label="Mobile number" value={phone} onChange={setPhone} placeholder="(719) 555-0100" autoFocus onEnter={save} testId="phone-input" className="flex-1" />
                     <Button onClick={save} disabled={!phoneOk} testId="save-phone">Save</Button>
                     <Button variant="secondary" onClick={() => { setPhone(owner.phone || ''); setEditingPhone(false) }}>Cancel</Button>
                  </div>
               ) : (
                  <>
                     <div className="flex-1 min-w-0">
                        {owner.phone ? <p className="text-[15px] font-semibold" data-testid="owner-phone">{owner.phone}</p> : <p className="text-[15px] text-[var(--fr-text-3)]">No mobile number on file</p>}
                        <p className="text-[13px] text-[var(--fr-text-2)]">Mobile · where we transfer leads</p>
                     </div>
                     <LinkButton onClick={() => setEditingPhone(true)} testId="change-phone">{owner.phone ? 'Not my number' : 'Add a number'}</LinkButton>
                  </>
               )}
            </Row>
         </RowCard>
         {picking && (
            <div className="mt-3" data-testid="owner-picker">
               <SearchPick
                  items={[owner, ...team].map((p) => ({ id: p.id, label: p.name, sub: p.email }))}
                  placeholder="Search your Follow Up Boss users"
                  onPick={(id) => {
                     const p = [owner, ...team].find((x) => x.id === id)
                     if (p) {
                        set('owner', p)
                        set('team', [...DEMO_TEAM.filter((x) => x.id !== id), ...(p.id !== 'u1' ? [DEMO_TEAM.find((x) => x.id === 'u1') || owner].filter(Boolean) : [])].filter((x) => x.id !== id))
                        setPhone(p.phone || '')
                     }
                     setPicking(false)
                  }}
                  testId="pick-owner"
               />
            </div>
         )}
         {calendar && (
            <p className="mt-4 text-center text-[13px] text-[var(--fr-text-2)] flex items-center justify-center gap-1.5" data-testid="calendar-auto">
               <span className="text-[var(--fr-good)]"><Icon name="check" size={15} /></span>
               {calendar === 'google' ? 'Google Calendar' : 'Outlook Calendar'} came with your sign-in. Appointments will land there.
            </p>
         )}
      </Screen>
   )
}

/* ---------- 3. Team ---------- */

export const TeamStep = () => {
   const s = useFirstRun()
   const { team, selectedTeam, invited, toggleTeam, invite, set, next, direction } = s
   const [inviteEmail, setInviteEmail] = useState('')
   const inviteOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)
   const seats = seatCount(s)
   const cost = SEAT_FIRST + (seats - 1) * SEAT_EXTRA
   const available = team.filter((p) => !selectedTeam.includes(p.id))
   useStepFooter(seats === 1 ? 'Continue with just me' : `Continue with ${seats} seats`, next, {
      hint: seats === 1 ? `$${SEAT_FIRST}/mo for one seat. You can always add teammates later from Settings.` : `$${SEAT_FIRST}/mo for you, $${SEAT_EXTRA}/mo per teammate · $${cost}/mo total.`,
      testId: 'continue-team',
   })
   const addInvite = () => {
      if (!inviteOk) return
      invite(inviteEmail.trim().toLowerCase())
      setInviteEmail('')
   }
   return (
      <Screen stepKey="team" direction={direction} kind="team" title="Who else should get leads?" lede="Pick teammates from Follow Up Boss or invite them by email. Leads are shared with everyone here. You can always add more teammates from Settings." width="sm">
         <FieldLabel>Add from Follow Up Boss</FieldLabel>
         <SearchPick
            items={available.map((p) => ({ id: p.id, label: p.name, sub: p.email }))}
            placeholder="Search by name or email"
            emptyLabel="Nobody else matches. Invite them by email below."
            onPick={toggleTeam}
            testId="team-search"
            renderItem={(r) => (
               <>
                  <Avatar name={r.label} size={28} />
                  <span className="flex-1 min-w-0 truncate">{r.label}</span>
                  <span className="text-[12px] text-[var(--fr-text-3)] truncate">{r.sub}</span>
               </>
            )}
         />
         <div className="mt-3 flex items-end gap-2">
            <Input label="Or invite by email" value={inviteEmail} onChange={setInviteEmail} placeholder="name@brokerage.com" type="email" onEnter={addInvite} testId="invite-email" className="flex-1" leading={<Icon name="mail" size={16} />} />
            <Button variant="secondary" onClick={addInvite} disabled={!inviteOk} testId="invite-add">Invite</Button>
         </div>
         {(selectedTeam.length > 0 || invited.length > 0) && (
            <RowCard className="mt-4" testId="team-list">
               {selectedTeam.map((id) => {
                  const p = team.find((x) => x.id === id)
                  if (!p) return null
                  return (
                     <Row key={id} className="min-h-[56px] py-1">
                        <Avatar name={p.name} size={32} />
                        <span className="flex-1 min-w-0">
                           <span className="block text-[14px] font-semibold truncate">{p.name}</span>
                           <span className="block text-[12px] text-[var(--fr-text-3)] truncate">{p.email}</span>
                        </span>
                        <span className="text-[12px] text-[var(--fr-text-3)]">+${SEAT_EXTRA}/mo</span>
                        <button type="button" aria-label={`Remove ${p.name}`} onClick={() => toggleTeam(id)} className="w-8 h-8 rounded-full text-[var(--fr-text-3)] hover:bg-[var(--fr-surface-2)] hover:text-[var(--fr-text)] flex items-center justify-center"><Icon name="close" size={14} /></button>
                     </Row>
                  )
               })}
               {invited.map((e) => (
                  <Row key={e} className="min-h-[56px] py-1">
                     <span className="w-8 h-8 rounded-full border border-dashed border-[var(--fr-border-strong)] flex items-center justify-center text-[var(--fr-text-3)]"><Icon name="mail" size={14} /></span>
                     <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-semibold truncate">{e}</span>
                        <span className="block text-[12px] text-[var(--fr-text-3)]">Invite goes out when you launch</span>
                     </span>
                     <span className="text-[12px] text-[var(--fr-text-3)]">+${SEAT_EXTRA}/mo</span>
                     <button type="button" aria-label={`Remove ${e}`} onClick={() => set('invited', invited.filter((x) => x !== e))} className="w-8 h-8 rounded-full text-[var(--fr-text-3)] hover:bg-[var(--fr-surface-2)] hover:text-[var(--fr-text)] flex items-center justify-center"><Icon name="close" size={14} /></button>
                  </Row>
               ))}
            </RowCard>
         )}
      </Screen>
   )
}

/* ---------- 3b. Calendar (email sign-ups only) ---------- */

export const CalendarStep = () => {
   const { calendar, set, next, direction, email } = useFirstRun()
   useStepFooter(calendar ? 'Continue' : 'Choose a calendar to continue', next, {
      disabled: !calendar,
      secondary: calendar ? null : { label: 'Skip for now, book appointments into Follow Up Boss', onClick: next },
      hint: calendar ? 'Appointments land on your calendar and in Follow Up Boss.' : 'This opens the provider’s sign-in. One tap.',
      testId: 'continue-calendar',
   })
   const Provider = ({ id, icon, label }: { id: 'google' | 'microsoft'; icon: React.ReactNode; label: string }) => {
      const on = calendar === id
      return (
         <button type="button" onClick={() => set('calendar', id)} data-testid={`calendar-${id}`} className={cn('w-full flex items-center gap-4 rounded-[20px] border bg-white px-5 h-[72px] text-left transition-all', on ? 'border-[var(--fr-good)] ring-4 ring-[var(--fr-good-tint)]' : 'border-[var(--fr-border)] hover:border-[var(--fr-border-strong)]')}>
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[var(--fr-surface-2)]">{icon}</span>
            <span className="flex-1 min-w-0">
               <span className="block text-[15px] font-semibold">{on ? `${label} connected` : `Continue with ${label}`}</span>
               <span className="block text-[13px] text-[var(--fr-text-2)] truncate">{on ? email || 'Bookings land here with the lead’s details' : 'Opens the sign-in, then comes straight back'}</span>
            </span>
            {on ? <span className="text-[var(--fr-good)]"><Icon name="check" size={20} /></span> : <span className="text-[var(--fr-text-3)]"><Icon name="arrow-right" size={18} /></span>}
         </button>
      )
   }
   return (
      <Screen stepKey="calendar" direction={direction} kind="calendar" title="Where should appointments go?" lede="When a lead is ready to meet and you can’t pick up, we book the appointment for you. Sign in to the calendar we should use." width="sm">
         <div className="space-y-3">
            <Provider id="google" icon={<GoogleG />} label="Google Calendar" />
            <Provider id="microsoft" icon={<MicrosoftM />} label="Outlook Calendar" />
         </div>
      </Screen>
   )
}

/* ---------- 4. Budget ---------- */

export const BudgetStep = () => {
   const s = useFirstRun()
   const { budget, set, next, direction } = s
   const preset = BUDGETS.find((b) => b.amount === budget)
   const [custom, setCustom] = useState(preset ? '' : String(budget))
   const seats = seatCount(s)
   const seatCost = SEAT_FIRST + (seats - 1) * SEAT_EXTRA
   useStepFooter(`Continue with $${budget}/mo`, next, { hint: `About ${leadsFor(budget)} leads worked each month, plus $${seatCost}/mo for ${seats} ${seats === 1 ? 'seat' : 'seats'}.`, testId: 'continue-budget' })
   const applyCustom = (v: string) => {
      const digits = v.replace(/\D/g, '').slice(0, 4)
      setCustom(digits)
      if (digits) set('budget', Math.min(5000, Math.max(50, Number(digits))))
   }
   return (
      <Screen stepKey="budget" direction={direction} kind="budget" title="How much should we spend on calls each month?" lede="This pays for the calls and texts themselves, not seats. Unused balance rolls over, and you can change it any time." width="lg">
         <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup">
            {BUDGETS.map((b) => {
               const on = !custom && budget === b.amount
               return (
                  <Tile key={b.id} selected={on} onClick={() => { setCustom(''); set('budget', b.amount) }} testId={`budget-${b.id}`} className="p-5">
                     <div className="flex items-center justify-between">
                        <p className="text-[13px] font-semibold text-[var(--fr-text-2)]">{b.label}{b.amount === 200 && <span className="ml-2 text-[11px] font-semibold text-[var(--fr-blue)]">Most popular</span>}</p>
                        <Radio on={on} />
                     </div>
                     <p className="mt-2 text-[30px] leading-none font-semibold tracking-tight">${b.amount}<span className="text-[14px] font-normal text-[var(--fr-text-3)]">/mo</span></p>
                     <p className="mt-3 text-[14px]"><b>{leadsFor(b.amount)}</b> leads we can work</p>
                     <p className="text-[12px] text-[var(--fr-text-3)] mt-0.5">{b.blurb}</p>
                  </Tile>
               )
            })}
         </div>
         <div className="mt-4 mx-auto max-w-[440px]">
            <Input label="Or type your own monthly budget" value={custom} onChange={applyCustom} placeholder="e.g. 400" leading={<span className="text-[15px] font-semibold">$</span>} testId="custom-budget" trailing={custom ? `about ${leadsFor(budget)} leads` : 'per month'} />
         </div>
      </Screen>
   )
}

/* ---------- 5. Payment ---------- */

export const PaymentStep = () => {
   const s = useFirstRun()
   const { budget, paid, set, next, direction } = s
   const [card, setCard] = useState({ number: '', exp: '', cvc: '', name: '', zip: '' })
   const [paying, setPaying] = useState(false)
   const seats = seatCount(s)
   const seatCost = SEAT_FIRST + (seats - 1) * SEAT_EXTRA
   const startBalance = Math.max(200, budget)
   const today = seatCost + startBalance
   const ready = paid || (card.number.replace(/\s/g, '').length >= 15 && card.exp.length >= 4 && card.cvc.length >= 3 && card.name.trim().length > 1 && card.zip.length === 5)
   const pay = () => {
      if (paid) return next()
      setPaying(true)
      setTimeout(() => {
         set('paid', true)
         setPaying(false)
         next()
      }, 900)
   }
   useStepFooter(paid ? 'Continue' : ready ? `Pay $${today} and continue` : 'Fill in your card to continue', pay, { disabled: !ready, loading: paying, hint: paid ? 'Paid. Your receipt is in your inbox.' : `Then $${seatCost}/mo for seats, plus whatever tops the balance back up to $${budget}.`, testId: 'pay' })
   const fmt = (v: string) => v.replace(/\D/g, '').slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ')
   return (
      <Screen stepKey="payment" direction={direction} kind="payment" title="Add your card" lede={<span data-testid="charge-summary">Charged today: {seats} {seats === 1 ? 'seat' : 'seats'} (${seatCost}) plus a ${startBalance} starting balance for calls, ${today} in total.</span>} width="sm">
         <div className="space-y-3">
            <Input label="Card number" value={card.number} onChange={(v) => setCard({ ...card, number: fmt(v) })} placeholder="1234 5678 9012 3456" leading={<Icon name="card" size={16} />} testId="card-number" />
            <div className="grid grid-cols-2 gap-3">
               <Input label="Expiry" value={card.exp} onChange={(v) => setCard({ ...card, exp: v.replace(/\D/g, '').slice(0, 4).replace(/(\d{2})(?=\d)/, '$1/') })} placeholder="MM/YY" testId="card-exp" />
               <Input label="Security code" value={card.cvc} onChange={(v) => setCard({ ...card, cvc: v.replace(/\D/g, '').slice(0, 4) })} placeholder="CVC" testId="card-cvc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
               <Input label="Name on card" value={card.name} onChange={(v) => setCard({ ...card, name: v })} placeholder="Sam Patel" testId="card-name" />
               <Input label="Billing ZIP" value={card.zip} onChange={(v) => setCard({ ...card, zip: v.replace(/\D/g, '').slice(0, 5) })} placeholder="80903" testId="card-zip" />
            </div>
            <p className="text-center text-[12px] text-[var(--fr-text-3)] flex items-center justify-center gap-1.5 pt-1"><Icon name="lock" size={12} /> Card details go straight to Stripe. We never see them.</p>
         </div>
      </Screen>
   )
}
